// Terms of Use acceptance helpers: the sign-in terms check (TermsGate) and the Terms page.
import { TERMS_VERSION } from '../constants/legal'

// Set when a sign-up request succeeds, so the first sign-in of that same account records that
// acceptance (context sign_up) instead of asking again. It names the account and expires, so
// another account signing in on this browser later is asked itself.
export const TERMS_SIGN_UP_KEY = 'highlights.termsAcceptedAtSignUp'
export const SIGN_UP_ACCEPTANCE_MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000

const storage = () => {
  try {
    return typeof window !== 'undefined' ? window.localStorage : null
  } catch (_) {
    return null
  }
}

const normalizedUsername = username => typeof username === 'string' ? username.trim().toLowerCase() : ''

export const rememberSignUpAcceptance = (username, now = Date.now()) => {
  const account = normalizedUsername(username)
  if (!account) return
  try {
    storage()?.setItem(TERMS_SIGN_UP_KEY, JSON.stringify({ version: TERMS_VERSION, username: account, at: now }))
  } catch (_) {}
}

export const clearSignUpAcceptance = () => {
  try {
    storage()?.removeItem(TERMS_SIGN_UP_KEY)
  } catch (_) {}
}

// The terms version accepted at sign-up by this account on this browser, or null. A record for
// another account, an old record or an unreadable one is removed, so it is never applied to
// anyone else.
export const signUpAcceptanceVersion = (username, now = Date.now()) => {
  const account = normalizedUsername(username)
  if (!account) return null
  let stored
  try {
    stored = storage()?.getItem(TERMS_SIGN_UP_KEY) || null
  } catch (_) {
    return null
  }
  if (!stored) return null
  let record = null
  try {
    record = JSON.parse(stored)
  } catch (_) {}
  const valid = record && typeof record === 'object' &&
    typeof record.version === 'string' &&
    record.username === account &&
    Number.isFinite(record.at) &&
    record.at <= now &&
    now - record.at < SIGN_UP_ACCEPTANCE_MAX_AGE_MS
  if (!valid) {
    clearSignUpAcceptance()
    return null
  }
  return record.version
}

// What the gate does with a GET /terms/status response. Only an explicit
// `accepted: false` blocks; anything unexpected (an error page, an empty body, a
// different shape) never locks people out of the app. When the API already asks for
// another version than this page shows (a terms update while the new page is still being
// published), the page cannot record it, so it does not block either.
export const termsGateDecision = (status, signUpVersion, currentVersion = TERMS_VERSION) => {
  if (!status || typeof status !== 'object' || Array.isArray(status)) return 'unknown'
  if (status.accepted === true) return 'accepted'
  if (status.accepted !== false) return 'unknown'
  if (typeof status.current_version === 'string' && status.current_version !== currentVersion) return 'outdated'
  if (signUpVersion && signUpVersion === currentVersion) return 'accept_sign_up'
  return 'ask'
}

// "Use the [contact form](/contact)." -> ['Use the ', { text: 'contact form', to: '/contact' }, '.']
// Only links to this site's own pages are recognised.
export const textWithLinks = text => {
  const parts = []
  const pattern = /\[([^\]]+)\]\((\/[^)\s]*)\)/g
  let last = 0
  let match
  while ((match = pattern.exec(text)) !== null) {
    if (match.index > last) parts.push(text.slice(last, match.index))
    parts.push({ text: match[1], to: match[2] })
    last = match.index + match[0].length
  }
  if (last < text.length) parts.push(text.slice(last))
  return parts
}

// Plain text of a paragraph, without link markup (for keys and for places that cannot link).
export const plainText = text => textWithLinks(text).map(part => typeof part === 'string' ? part : part.text).join('')
